import React from "react";
import { Trash2 } from "lucide-react";
import { cn } from "../../../lib/utils";

import ButtonV2 from "../../../components/ui/button/v2";
import FormField, { useTouched } from "../../create-case/components/party-involved/FormField";
import AccountNameSearchInput, { StakeholderOption } from "../../creditAccounts/components/AccountNameSearchInput";
import {
  Account,
  AccountCategory,
  AccountType,
  ACCOUNT_CATEGORIES,
  ACCOUNT_TYPE_OPTIONS,
  buildAccountCategoryOptions,
} from "../../../api/accounts";
import { FieldErrors } from "../../create-case/utils/creditValidation";

export type AccountWithStakeholder = Account & {
  stakeholder_id?: string;
};

export interface AccountRowProps {
  account: AccountWithStakeholder;
  errors: FieldErrors;
  stakeholderOptions: StakeholderOption[];
  showLabels: boolean;
  showErrors: boolean;
  disabled: boolean;
  canDelete: boolean;
  onAccountChange: (patch: Partial<AccountWithStakeholder>) => void;
  onAddStakeholder: (name: string) => void;
  onDelete: () => void;
}

export const getAccountRowErrors = (
  row: AccountWithStakeholder,
  all: AccountWithStakeholder[],
): FieldErrors => {
  const errors: FieldErrors = {};
  if (!row.ext_account_number?.trim()) {
    errors.extAccountNumber = "Account number is required.";
  } else if (
    all.filter(
      (a) =>
        a.ext_account_number?.trim().toLowerCase() ===
        row.ext_account_number?.trim().toLowerCase(),
    ).length > 1
  ) {
    errors.extAccountNumber = "Account number must be unique.";
  }
  if (!row.account_type) errors.accountType = "Account type is required.";
  if (!row.account_category) {
    errors.accountCategory = "Account category is required.";
  } else if (
    row.account_type &&
    !(ACCOUNT_CATEGORIES[row.account_type as AccountType] ?? []).includes(
      row.account_category as AccountCategory,
    )
  ) {
    errors.accountCategory = "Account category is not valid for this type.";
  }
  if (!row.stakeholder_id) {
    errors.accountName =
      "Please select an existing stakeholder or click + Add to create a new one.";
  } else if (!row.account_name?.trim()) {
    errors.accountName = "Account name is required.";
  }
  return errors;
};

const AccountRow: React.FC<AccountRowProps> = ({
  account: row,
  errors,
  stakeholderOptions,
  showLabels,
  showErrors,
  disabled,
  canDelete,
  onAccountChange,
  onAddStakeholder,
  onDelete,
}) => {
  const { isTouched, markTouched } = useTouched();
  const err = (f: string) => (showErrors || isTouched(f) ? errors[f] : undefined);
  const categoryOptions = buildAccountCategoryOptions(row.account_type || "");

  return (
    <div
      className={cn(
        "grid gap-3 items-start p-3 -mx-3 rounded-md transition-colors duration-150 hover:bg-[#F3F4F6] grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto]",
      )}
    >
      <FormField
        type="text"
        label="Account Number"
        required
        placeholder="Enter account number"
        value={row.ext_account_number || ""}
        error={err("extAccountNumber")}
        disabled={disabled}
        hideLabel={!showLabels}
        onChange={(v) => onAccountChange({ ext_account_number: v })}
        onBlur={() => markTouched("extAccountNumber")}
      />
      <FormField
        type="select"
        label="Account Type"
        required
        placeholder="Select type"
        value={row.account_type || ""}
        options={ACCOUNT_TYPE_OPTIONS}
        error={err("accountType")}
        disabled={disabled}
        hideLabel={!showLabels}
        onChange={(v) =>
          onAccountChange({
            account_type: v as AccountType,
            account_category: "" as AccountCategory,
          })
        }
        onBlur={() => markTouched("accountType")}
      />
      <FormField
        type="select"
        label="Account Category"
        required
        placeholder="Select category"
        value={row.account_category || ""}
        options={categoryOptions}
        error={err("accountCategory")}
        disabled={disabled || !row.account_type}
        hideLabel={!showLabels}
        onChange={(v) => onAccountChange({ account_category: v as AccountCategory })}
        onBlur={() => markTouched("accountCategory")}
      />
      <AccountNameSearchInput
        accountName={row.account_name || ""}
        selectedStakeholderId={row.stakeholder_id}
        options={stakeholderOptions}
        onSelectStakeholder={(stakeholderId) =>
          onAccountChange({ stakeholder_id: stakeholderId })
        }
        onAddStakeholder={onAddStakeholder}
        roleLabel="Primary Account Holder"
        required
        disabled={disabled}
        hideLabel={!showLabels}
        error={err("accountName")}
        onBlur={() => markTouched("accountName")}
      />
      <div className={cn("flex items-center", showLabels && "pt-5")}>
        <ButtonV2
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={disabled || !canDelete}
          title="Delete account"
          className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
          prefix={<Trash2 width="16" height="16" />}
        />
      </div>
    </div>
  );
};

export default AccountRow;
